import React, { useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
} from "@mui/icons-material";

const isMidnight = (value) => dayjs(value).format("HH:mm:ss") === "00:00:00";

/**
 * "Nov 3, 2026" / "Nov 3 – Nov 7, 2026" for whole days (the end is the
 * exclusive midnight after the last day off), otherwise date and times.
 */
export function formatTimeOffRange(block) {
  const start = dayjs(block.start);
  const end = dayjs(block.end);
  if (isMidnight(block.start) && isMidnight(block.end)) {
    const lastDay = end.subtract(1, "day");
    if (lastDay.isSame(start, "day")) return start.format("MMM D, YYYY");
    return `${start.format("MMM D, YYYY")} – ${lastDay.format("MMM D, YYYY")}`;
  }
  if (end.isSame(start, "day")) {
    return `${start.format("MMM D, YYYY h:mm A")} – ${end.format("h:mm A")}`;
  }
  return `${start.format("MMM D, YYYY h:mm A")} – ${end.format(
    "MMM D, YYYY h:mm A"
  )}`;
}

const headCellSx = {
  fontWeight: 600,
  py: 0.75,
  color: "common.white",
  bgcolor: "secondary.main",
};

/**
 * A stylist's time-off blocks (JD-465) with edit and delete actions when
 * `canManage`. `onDelete(block)` may return a promise; the confirm dialog
 * stays open (busy) until it settles.
 */
export default function TimeOffList({
  blocks = [],
  onEdit,
  onDelete,
  canManage = false,
}) {
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await onDelete?.(pendingDelete);
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  if (!blocks.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        No time off recorded.
      </Typography>
    );
  }

  return (
    <>
      <TableContainer
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "background.paper",
        }}
      >
        <Table size="small" aria-label="stylist time off table">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Dates</TableCell>
              <TableCell sx={headCellSx}>Note</TableCell>
              <TableCell sx={headCellSx}>Recorded by</TableCell>
              {canManage ? (
                <TableCell sx={{ ...headCellSx, width: 96 }} align="right" />
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {blocks.map((block) => (
              <TableRow
                key={block.id}
                sx={{ "&:last-child td": { borderBottom: 0 } }}
              >
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="body2">
                    {formatTimeOffRange(block)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="body2">{block.note || "—"}</Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="body2">
                    {block.created_by_name || "—"}
                  </Typography>
                </TableCell>
                {canManage ? (
                  <TableCell sx={{ py: 0.5 }} align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        aria-label="Edit time off"
                        onClick={() => onEdit?.(block)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Delete time off"
                        onClick={() => setPendingDelete(block)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TimeOffDeleteDialog
        block={pendingDelete}
        loading={deleting}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}

/** Confirm deleting one time-off block (pattern: Edit-A-Client/DeleteConfirmDialog). */
export function TimeOffDeleteDialog({ block, loading, onClose, onConfirm }) {
  return (
    <Dialog
      open={Boolean(block)}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          bgcolor: "secondary.main",
          color: "common.white",
          borderBottom: "1px solid",
          borderColor: "primary.main",
          px: 3,
          py: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="inherit">
            Delete time off
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255, 255, 255, 0.75)", mt: 0.25 }}
          >
            The stylist becomes bookable again for this time.
          </Typography>
        </Box>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={loading}
          size="small"
          sx={{
            color: "primary.main",
            mt: 0.25,
            "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <Typography sx={{ mb: 2, mt: 1 }}>
          {block
            ? `Delete the time off on ${formatTimeOffRange(block)}${
                block.note ? ` (${block.note})` : ""
              }? Appointments are not affected.`
            : ""}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Button
          type="button"
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          color="secondary"
          sx={{ textTransform: "none", minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{ textTransform: "none", fontWeight: 700, minWidth: 100 }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}
